import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Upload, ChevronDown, ChevronUp, Lock, Crown } from 'lucide-react';
import VisitingCardDisplay from './VisitingCardDisplay';
import ImageCropDialog from './ImageCropDialog';
import { useToast } from '../hooks/use-toast';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import { HexColorPicker } from 'react-colorful';
import UpgradeModal from './UpgradeModal';

const CustomizeCardModal = ({ open, onOpenChange, card, onSave }) => {
  const { toast } = useToast();
  const { isSubscribed } = useSubscriptionLimits();
  const [customization, setCustomization] = useState({
    backgroundImage: card?.backgroundImage || null,
    qrColor: card?.qrColor || '#0066CC',
    textColor: card?.textColor || '#1F2937',
    fontFamily: card?.fontFamily || 'arial'
  });
  const [saving, setSaving] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [hexInputs, setHexInputs] = useState({
    qrColor: card?.qrColor || '#0066CC',
    textColor: card?.textColor || '#1F2937'
  });
  const [showQRPicker, setShowQRPicker] = useState(false);
  const [showTextPicker, setShowTextPicker] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Validate and update hex color
  const handleHexInput = (type, value) => {
    // Block QR color changes for free users
    if (type === 'qrColor' && !isSubscribed) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Allow typing # and hex characters
    const cleanValue = value.toUpperCase();
    setHexInputs(prev => ({ ...prev, [type]: cleanValue }));
    
    // Validate complete hex color (3 or 6 digits)
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexRegex.test(cleanValue)) {
      setCustomization(prev => ({ ...prev, [type]: cleanValue }));
    }
  };

  // Handle QR color picker click
  const handleQRColorClick = () => {
    if (!isSubscribed) {
      setShowUpgradeModal(true);
      return;
    }
    setShowQRPicker(!showQRPicker);
  };

  // Sync customization state when card prop changes or modal opens
  React.useEffect(() => {
    if (card && open) {
      const qrColor = card.qrColor || '#0066CC';
      const textColor = card.textColor || '#1F2937';
      setCustomization({
        backgroundImage: card.backgroundImage || null,
        qrColor,
        textColor,
        fontFamily: card.fontFamily || 'arial'
      });
      setHexInputs({
        qrColor,
        textColor
      });
    }
  }, [card, open]);

  // Create preview card with current customization
  const previewCard = {
    ...card,
    backgroundImage: customization.backgroundImage,
    qrColor: customization.qrColor,
    textColor: customization.textColor,
    fontFamily: customization.fontFamily
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(customization);
      toast({
        title: "Customization Saved!",
        description: "Your card has been updated."
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving customization:', error);
      toast({
        title: "Error",
        description: "Failed to save customization. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center text-xl sm:text-2xl">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-blue-600" />
            Customize Your Card
          </DialogTitle>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
            Choose colors and style with live preview
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 py-3 sm:py-4">
          {/* Left: Customization Controls - 33% width on desktop */}
          <div className="space-y-5 sm:space-y-6 order-2 lg:order-1 lg:col-span-1">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Style Options</h3>
            
            {/* QR Color */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                QR Code Color
                {!isSubscribed && (
                  <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                    <Crown className="w-3 h-3" />
                    Pro
                  </span>
                )}
              </label>
              
              {/* Color Display & Hex Input */}
              <div className="flex gap-2 mb-3 relative">
                <button
                  type="button"
                  onClick={handleQRColorClick}
                  className={`w-16 h-12 rounded-lg border-2 flex-shrink-0 transition-all relative ${
                    !isSubscribed 
                      ? 'border-gray-200 cursor-not-allowed opacity-60' 
                      : 'border-gray-300 cursor-pointer hover:border-blue-500'
                  }`}
                  style={{ backgroundColor: !isSubscribed ? '#454440' : customization.qrColor }}
                  disabled={!isSubscribed}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    {!isSubscribed ? (
                      <Lock className="w-4 h-4 text-white drop-shadow-lg" />
                    ) : showQRPicker ? (
                      <ChevronUp className="w-5 h-5 text-white drop-shadow-lg" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-white drop-shadow-lg" />
                    )}
                  </div>
                </button>
                <input
                  type="text"
                  value={!isSubscribed ? '#454440' : hexInputs.qrColor}
                  onChange={(e) => handleHexInput('qrColor', e.target.value)}
                  onClick={() => !isSubscribed && setShowUpgradeModal(true)}
                  placeholder="#454440"
                  className={`flex-1 px-3 py-2 border-2 rounded-lg font-mono text-sm ${
                    !isSubscribed
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                      : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  maxLength={7}
                  readOnly={!isSubscribed}
                />
              </div>

              {!isSubscribed && (
                <p className="text-xs text-gray-500 mb-3">
                  Free users get Piper Card branded QR code. Upgrade to customize colors and remove branding.
                </p>
              )}

              {/* Advanced Color Picker */}
              {showQRPicker && isSubscribed && (
                <div className="mb-3 p-3 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                  <HexColorPicker
                    color={customization.qrColor}
                    onChange={(color) => {
                      setCustomization(prev => ({ ...prev, qrColor: color }));
                      setHexInputs(prev => ({ ...prev, qrColor: color }));
                    }}
                    style={{ width: '100%', height: '200px' }}
                  />
                </div>
              )}
            </div>

            {/* Text Color */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">Text Color</label>
              
              {/* Color Display & Hex Input */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setShowTextPicker(!showTextPicker)}
                  className="w-16 h-12 rounded-lg border-2 border-gray-300 cursor-pointer flex-shrink-0 hover:border-blue-500 transition-all relative"
                  style={{ backgroundColor: customization.textColor }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    {showTextPicker ? (
                      <ChevronUp className="w-5 h-5 text-white drop-shadow-lg" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-white drop-shadow-lg" />
                    )}
                  </div>
                </button>
                <input
                  type="text"
                  value={hexInputs.textColor}
                  onChange={(e) => handleHexInput('textColor', e.target.value)}
                  placeholder="#1F2937"
                  className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  maxLength={7}
                />
              </div>

              {/* Advanced Color Picker */}
              {showTextPicker && (
                <div className="mb-3 p-3 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                  <HexColorPicker
                    color={customization.textColor}
                    onChange={(color) => {
                      setCustomization(prev => ({ ...prev, textColor: color }));
                      setHexInputs(prev => ({ ...prev, textColor: color }));
                    }}
                    style={{ width: '100%', height: '200px' }}
                  />
                </div>
              )}
            </div>

            {/* Font Family */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">Font Style</label>
              <select
                value={customization.fontFamily}
                onChange={(e) => setCustomization(prev => ({ ...prev, fontFamily: e.target.value }))}
                className="w-full p-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="arial">Arial - Modern & Clean</option>
                <option value="playfair">Playfair Display - Elegant</option>
                <option value="cinzel">Cinzel - Classic</option>
                <option value="cormorant">Cormorant - Sophisticated</option>
              </select>
            </div>

            {/* Background Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">Background Image</label>
              
              <input
                type="file"
                id="background-upload"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setImageToCrop(reader.result);
                      setCropDialogOpen(true);
                    };
                    reader.readAsDataURL(file);
                  }
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full border-2 border-dashed border-gray-300 hover:border-blue-500 py-4 sm:py-6 text-sm sm:text-base"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('background-upload').click();
                }}
              >
                <Upload className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {customization.backgroundImage ? 'Change Background' : 'Upload Background'}
              </Button>
              {customization.backgroundImage && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-2 border-2 border-red-300 text-red-600 hover:bg-red-50 py-3 text-sm sm:text-base"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCustomization(prev => ({ ...prev, backgroundImage: null }));
                  }}
                >
                  Remove Background
                </Button>
              )}
            </div>
          </div>

          {/* Right: Live Preview - 66% width on desktop */}
          <div className="space-y-2 sm:space-y-3 order-1 lg:order-2 lg:col-span-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Live Preview</h3>
            <div className="lg:sticky lg:top-4">
              <VisitingCardDisplay card={previewCard} hideQR={false} />
            </div>
          </div>
        </div>

        {/* Image Crop Dialog */}
        <ImageCropDialog
          open={cropDialogOpen}
          onOpenChange={setCropDialogOpen}
          imageSrc={imageToCrop}
          onCropComplete={(croppedImageBlob) => {
            // Convert blob to base64
            const reader = new FileReader();
            reader.onloadend = () => {
              setCustomization(prev => ({ ...prev, backgroundImage: reader.result }));
            };
            reader.readAsDataURL(croppedImageBlob);
            setCropDialogOpen(false);
          }}
          aspectRatio={16 / 9}
          shape="rect"
        />

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white order-1 sm:order-2"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        feature="custom QR code colors"
      />
    </Dialog>
  );
};

export default CustomizeCardModal;
